import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MNote, MRow } from '@/components/mobile/MobileKit';
import { useTranslation } from 'react-i18next';

import { progressApi, type LeaderboardEntry } from '@/api/progress';
import { usersApi } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';

export function StudentLeaderboardPage({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const meId = useAuthStore((s) => s.user?.id);
  const [marathonId, setMarathonId] = useState<string>('');

  // Scope options come from the student's ENROLLMENTS (incl. PRIVATE marathons),
  // not the public catalog — otherwise a private-only student could never pick
  // their own marathon's leaderboard.
  const marathons = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });

  // The leaderboard is ALWAYS scoped to a marathon (customer 05.08: points are
  // per-marathon, no global mix). Default to the student's first enrolled
  // marathon once the list loads.
  /**
   * ⚠️ РЕЙТИНГ ВСЕГДА ПО ОСНОВНОМУ КУРСУ, И ВЫБОРА БОЛЬШЕ НЕТ.
   *
   * Стоял выпадающий список из ВСЕХ курсов ученика — а вместе с тарифом ему
   * выдаются мини-курсы: «Материалы», «Мотивация», «Занятия с психологом».
   * Заказчик 07.09.2026 открыл список, увидел их все и сказал: «рейтинг идёт
   * только по основному марафону, выбора марафона не должно быть».
   *
   * Он прав по сути, а не только по виду. Очки начисляются за учёбу в языковом
   * курсе; в мини-курсе их почти нет, и рейтинг там показывает не соревнование,
   * а пустоту. Ученик выбирал «Мотивацию», видел нули у всех и решал, что
   * рейтинг сломан.
   *
   * Основной курс у ученика ровно один — это правило БАЗЫ (уникальный индекс на
   * `is_primary`), а не соглашение. Откат на первый курс оставлен на случай
   * рассинхрона данных: показать хоть какой-то рейтинг лучше, чем пустой экран.
   */
  useEffect(() => {
    if (!marathonId && marathons.data?.length) {
      const primary = marathons.data.find((m) => m.is_primary) ?? marathons.data[0];
      setMarathonId(primary.id);
    }
  }, [marathons.data, marathonId]);

  /**
   * ⚠️ БЕРЁМ ОКНО, А НЕ ПЕРВЫЕ 50 СТРОК. Раньше экран забирал верхушку и рисовал
   * её подряд: на потоке в 814 учеников человек с двухсотым местом не видел ни
   * себя, ни соседей — только чужих. Теперь сервер отдаёт верхушку И кусок
   * вокруг ученика (решение заказчика 06.09.2026).
   */
  const lb = useQuery({
    queryKey: ['leaderboard-window', marathonId],
    queryFn: () => progressApi.getLeaderboardWindow(marathonId!),
    enabled: !!marathonId,
  });
  const myRank = useQuery({
    queryKey: ['my-rank', marathonId],
    queryFn: () => progressApi.getMyRank(marathonId),
    enabled: !!marathonId,
  });

  const top = lb.data?.top ?? [];
  const around = lb.data?.around ?? [];
  // Разрыв показываем, только когда между верхушкой и окном есть пропуск: без
  // него «5, 14, 15» читается как ошибка нумерации, а с ним — как «дальше по
  // списку».
  const hasGapRow = around.length > 0 && around[0].rank > (top[top.length - 1]?.rank ?? 0) + 1;
  const entries = [...top, ...around];
  // Podium = top-3 in visual order [2nd, 1st, 3rd] so 1st sits centered/tallest.
  const top3 = entries.slice(0, 3);
  const podium = [top3[1], top3[0], top3[2]].filter(Boolean) as LeaderboardEntry[];
  const rest = entries.slice(3);

  // ── Телефон: своя строка сверху, остальные — одним блоком ────────────────
  // Пьедестала в макете нет намеренно: на узком экране он съедает пол-экрана
  // ради трёх имён, а человеку важнее своё место и кто рядом.
  if (isMobile) {
    const mine = myRank.data;
    return (
      <div className="space-y-3.5">
        {/* ⚠️ ПЕРЕКЛЮЧАТЕЛЯ КУРСОВ ЗДЕСЬ БОЛЬШЕ НЕТ — как и на компьютере.
            Заказчик 07.09.2026: «как на мобиле, так и на десктопе». Причина
            описана у выбора основного курса выше: в списке оказывались
            мини-курсы из тарифа, где очков нет по определению. */}
        {mine && (
          <div className="flex items-center gap-3 rounded-[26px] bg-surface-container-low px-4 py-3.5">
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
                {t('leaderboard.myPlace')}
              </div>
              <div className="mt-0.5 font-display text-[17px] font-bold text-on-surface">
                {t('leaderboard.rankValue', { rank: mine.rank })}
                {(() => {
                  const row = entries.find((x) => x.student_id === meId);
                  return row ? ` · ${row.total_points}` : '';
                })()}
              </div>
            </div>
          </div>
        )}
        <MBlock>
          {entries.map((e, i) => {
            const isMe = e.student_id === meId;
            // ⚠️ РАЗРЫВ ОБЯЗАТЕЛЕН. Между верхушкой и окном вокруг ученика
            // пропущены десятки мест; без пометки «5, 14, 15» читается как сбой
            // нумерации, а не как «дальше по списку».
            const showGap = hasGapRow && i === top.length;
            return (
              <div key={e.student_id}>
                {showGap && (
                  <div className="py-1.5 text-center text-[13px] font-bold tracking-[0.25em] text-on-surface-variant">
                    ···
                  </div>
                )}
              <MRow
                leading={
                  <span
                    className={cn(
                      'grid h-8 w-8 flex-none place-items-center rounded-full text-[11px] font-bold',
                      i === 0
                        ? 'bg-primary-container text-on-primary-container'
                        : isMe
                          ? 'bg-primary text-on-primary'
                          : 'bg-info-container text-info',
                    )}
                  >
                    {e.rank}
                  </span>
                }
                title={isMe ? t('leaderboard.you') : (e.student_name ?? '—')}
                trailing={
                  <span
                    className={cn(
                      'flex-none font-display text-[13px] font-bold',
                      isMe ? 'text-on-primary-container' : 'text-on-surface',
                    )}
                  >
                    {e.total_points}
                  </span>
                }
                last={i === entries.length - 1}
              />
              </div>
            );
          })}
        </MBlock>

        {/* Сколько не хватает до пятёрки — строка из макета. Считаем по тем же
            данным, что уже на экране: очки пятого места минус свои. Показываем,
            только когда человек ниже пятого и пятое место вообще есть. */}
        {(() => {
          const fifth = entries.find((x) => x.rank === 5);
          const my = entries.find((x) => x.student_id === meId);
          if (!fifth || !my || my.rank <= 5) return null;
          const gap = fifth.total_points - my.total_points;
          if (gap <= 0) return null;
          return (
            <MNote
              icon="trending_up"
              title={t('leaderboard.gapToTop', { points: gap })}
              meta={t('leaderboard.gapHint')}
            />
          );
        })()}
      </div>
    );
  }

  // Сколько очков до пятого места. Показываем, только если я ниже пятого и
  // пятое место видно в списке: иначе это была бы придуманная цифра.
  const fifth = entries.find((e) => e.rank === 5);
  const myEntry = entries.find((e) => e.student_id === meId);
  // Место знаем всегда, очки — только если я попал в видимый список: отдельный
  // запрос своего места возвращает номер без очков.
  const myPlace = myEntry?.rank ?? myRank.data?.rank ?? 0;
  const myPoints = myEntry?.total_points;
  const toTop5 = fifth && myPlace > 5 && myPoints != null ? fifth.total_points - myPoints : 0;

  return (
    <div>
      <div className={cn('mb-3.5 flex items-end gap-3', embedded ? 'justify-end' : 'justify-between')}>
        {!embedded && (
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-display">{t('leaderboard.title')}</h1>
            <p className="mt-0.5 text-sm text-on-surface-variant">{t('leaderboard.subtitle')}</p>
          </div>
        )}
        {/* ⚠️ ВЫПАДАЮЩЕГО СПИСКА КУРСОВ ЗДЕСЬ БОЛЬШЕ НЕТ — см. пояснение к
            выбору основного курса выше. */}
      </div>

      {lb.isLoading ? (
        <div className="h-40 animate-pulse rounded-[20px] bg-surface-container" />
      ) : lb.isError ? (
        <div className="rounded-[20px] bg-surface-container px-[18px] py-4 text-center text-error">
          {humanizeError(lb.error)}
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-[20px] bg-surface-container px-[18px] py-4 text-center text-on-surface-variant">
          {t('leaderboard.empty')}
        </div>
      ) : (
        <>
          {/* ── Моё место ───────────────────────────────────────────────── */}
          {myPlace > 0 && (
            <div className="mb-3.5 flex items-center gap-3 rounded-[20px] bg-surface-container px-5 py-4">
              <div className="flex-1">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
                  {t('leaderboard.myRank')}
                </div>
                <div className="mt-1 font-display text-[19px] font-bold text-on-surface">
                  {myPoints != null
                    ? t('leaderboard.placeAndPoints', { place: myPlace, points: myPoints })
                    : t('leaderboard.placeOnly', { place: myPlace })}
                </div>
              </div>
            </div>
          )}

          {/* ── Пьедестал ───────────────────────────────────────────────── */}
          {podium.length > 0 && (
            <div className="flex items-end gap-3 border-b-2 border-[rgb(200_183_155)] px-1">
              {podium.map((e) => (
                <PodiumColumn key={e.student_id} entry={e} isMe={e.student_id === meId} t={t} />
              ))}
            </div>
          )}

          {/* ── Остальные ───────────────────────────────────────────────── */}
          {rest.length > 0 && (
            <div className="mt-3.5 rounded-[20px] bg-surface-container px-[18px] py-0.5">
              {rest.map((entry, ri) => {
                const isMe = entry.student_id === meId;
                // Разрыв ставим там же, где и на телефоне; пьедестал забрал
                // первые три места, поэтому смещаем на три.
                const showGap = hasGapRow && ri === top.length - 3;
                return (
                  <div key={entry.student_id}>
                  {showGap && (
                    <div className="py-1.5 text-center text-[13px] font-bold tracking-[0.25em] text-on-surface-variant">
                      ···
                    </div>
                  )}
                  <div
                    className={cn(
                      'flex items-center gap-3 border-b border-outline py-3 last:border-b-0',
                      isMe && '-mx-[18px] rounded-[18px] border-b-0 bg-primary-container px-[18px] py-3.5',
                    )}
                  >
                    <span
                      className={cn(
                        'w-6 flex-none text-[13px] font-bold tabular-nums',
                        isMe ? 'text-on-primary-container' : 'text-on-surface-variant',
                      )}
                    >
                      {entry.rank}
                    </span>
                    <Avatar name={entry.student_name} src={entry.avatar_url} size="sm" />
                    <span className={cn('min-w-0 flex-1 truncate text-[14px] text-on-surface', isMe ? 'font-bold' : 'font-semibold')}>
                      {isMe ? t('leaderboard.you') : (entry.student_name ?? t('leaderboard.anonymous'))}
                    </span>
                    <span
                      className={cn(
                        'flex-none text-[13px] font-bold tabular-nums',
                        isMe ? 'text-on-primary-container' : 'text-on-surface',
                      )}
                    >
                      {entry.total_points}
                    </span>
                  </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Сколько до пятёрки ──────────────────────────────────────── */}
          {toTop5 > 0 && (
            <div className="mt-3.5 rounded-[20px] bg-info-container px-[18px] py-3.5">
              <div className="text-[13.5px] font-semibold text-on-surface">
                {t('leaderboard.toTop5', { points: toTop5 })}
              </div>
              <div className="text-[12.5px] text-on-surface-variant">{t('leaderboard.toTop5Hint')}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Ступень пьедестала по макету «Desktop Web v2»: над ступенью — кружок,
 * имя и очки, в самой ступени — номер. Высота и цвет зависят от места.
 */
function PodiumColumn({
  entry,
  isMe,
  t,
}: {
  entry: LeaderboardEntry;
  isMe: boolean;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const rank = entry.rank;
  const first = rank === 1;
  // Ступени: первая выше и оранжевая, вторая и третья — тонами песка.
  const height = first ? 'h-[104px]' : rank === 2 ? 'h-[76px]' : 'h-14';
  const pedestal = first
    ? 'bg-primary-pressed text-white text-[22px]'
    : rank === 2
      ? 'bg-outline text-[#4A4139] text-[18px]'
      : 'bg-[rgb(200_183_155)] text-[#4A4139] text-[18px]';

  return (
    <div className="flex flex-1 flex-col items-center">
      <div className="flex h-[104px] flex-col items-center justify-end">
        <Avatar
          name={entry.student_name}
          src={entry.avatar_url}
          size={first ? 'lg' : 'md'}
          className={cn(
            first ? 'h-[54px] w-[54px] bg-primary-container text-[15px] text-on-primary-container' : 'h-[46px] w-[46px] text-[13px]',
            !first && (rank === 2 ? 'bg-info-container text-info' : 'bg-surface-container-high text-on-surface-variant'),
            isMe && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
          )}
        />
        <p className="mt-2 line-clamp-1 max-w-full text-center text-[12px] font-semibold text-on-surface">
          {isMe ? t('leaderboard.you') : (entry.student_name ?? t('leaderboard.anonymous'))}
        </p>
        <p className="mt-0.5 text-[11px] text-on-surface-variant tabular-nums">{entry.total_points}</p>
      </div>
      <div
        className={cn(
          'mt-2.5 flex w-full justify-center rounded-t-[14px] pt-[11px] font-display font-bold',
          height,
          pedestal,
        )}
      >
        {rank}
      </div>
    </div>
  );
}
